import { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { PROFILE } from "@/constants/testIds";

const TIER_LABEL = { free: "Free", creator: "Creator · $4.99/mo", pro: "Pro · $9.99/mo" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "");

const CancelButton = ({ until }) => {
  const { request, refresh } = useProfile();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await request("post", "/payments/cancel");
      await refresh();
      toast.success("Membership canceled. You won't be billed again.");
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid="membership-cancel-button" disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-nw-red transition-colors">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Cancel membership
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="membership-cancel-dialog" className="bg-nw-surface border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Cancel your membership?</AlertDialogTitle>
          <AlertDialogDescription>PayPal won't bill you again. {until ? `You can keep uploading until ${fmtDate(until)}.` : ""}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="membership-cancel-keep">Keep plan</AlertDialogCancel>
          <AlertDialogAction data-testid="membership-cancel-confirm" onClick={cancel} className="bg-nw-red hover:bg-nw-red/90">Cancel membership</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const MembershipCard = () => {
  const { profile, membership, freeUploads } = useProfile();
  const isCreator = profile.account_type === "creator";
  const inForce = !!profile.plan_badge;
  const status = membership?.status;
  const end = membership?.current_period_end;
  const note = inForce && status === "active" ? `Active · renews ${fmtDate(end) || "monthly"} through PayPal.`
    : inForce ? `Canceled · uploads stay on until ${fmtDate(end)}.`
    : status === "past_due" ? "Your last PayPal payment failed. Update it in PayPal to keep uploading."
    : isCreator && freeUploads ? "Founding creator — your uploads stay free."
    : isCreator ? "Subscribe to a plan to upload." : "Fans are free forever.";
  return (
    <div className="rounded-2xl border border-white/10 bg-nw-surface/60 p-5 space-y-2">
      <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Crown className="h-4 w-4 text-nw-red" /> Membership</p>
      <p data-testid={PROFILE.membership} className="font-display text-xl font-bold text-white">{TIER_LABEL[inForce ? membership.tier : "free"]}</p>
      <p data-testid="profile-membership-note" className="text-xs text-slate-500">{note}</p>
      {inForce && status === "active" && <CancelButton until={end} />}
      {!inForce && !freeUploads && (
        <Link data-testid="profile-see-plans" to="/upload" className="inline-block text-sm font-semibold text-nw-red hover:underline">See Creator & Pro plans</Link>
      )}
    </div>
  );
};
